import React, {forwardRef, useEffect, useImperativeHandle, useRef, useState} from "react";
import {
    Alert,
    Divider,
    FormControl, FormControlLabel, IconButton,
    InputLabel,
    MenuItem,
    InputAdornment,
    Select, Snackbar,
    Stack,
    Switch,
    TextField,
    ThemeProvider, Tooltip, Typography
} from "@mui/material";
import Button from "@mui/material/Button";
import HubIcon from "@mui/icons-material/Hub";
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import './Sidebar.css';
import {Delete, SaveAsSharp} from "@mui/icons-material";
import PercentIcon from '@mui/icons-material/Percent';
import Collapsable from "./Collapsable";
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import * as d3 from "d3";
import RemoveIcon from '@mui/icons-material/Remove';
import {LargeGraphDialog} from "./Dialog.js";
import {getCurrentUrl} from "./Globals";


function validateInputsRegex (e, setter, regex) {
    const re = regex;
    if (e.target.value === '' || re.test(e.target.value)) {
        setter(e.target.value);
    }
}
const EdgeLabelPercentage = (props) => {
    const myEdgeLabel = props.edgeLabels[props.id];
    const [label, setLabel] = useState(myEdgeLabel.label);
    const [percentage, setPercentage] = useState(myEdgeLabel.percentage);
    const [color, setColor] = useState(myEdgeLabel.color);
    const saveChanges = () => {
        props.setEdgeLabels(prevLabels => prevLabels.map((obj, index) => {
            if (index === props.id) {
                return {...obj, label: label, percentage: percentage, color: color}
            } else return obj
        }))
    }
    const deleteEdgeLabel = (id) => {
        return () => {
            let tempLabel = [...props.edgeLabels];
            tempLabel.splice(id, 1);
            props.setEdgeLabels(tempLabel);
        }
    }
    return (
        <div>
            <Stack direction="row" fullwidth="true" sx={{mt: 1}} className="flex flex-row justify items-center">
                <TextField id="outlined-basic" label="Label"
                           variant="outlined"
                           sx={{flex: 6}}
                           size="small"
                           value={label}
                           onChange={e => setLabel(e.target.value)}
                           onBlur={saveChanges}/>
                <TextField id="outlined-basic" label="Percentage"
                           variant="outlined"
                           size="small"
                           sx={{flex: 3}}
                           value={percentage}
                           onChange={e => validateInputsRegex(e, setPercentage, /^(0|[1-9][0-9]?|100)$/)}
                           onBlur={saveChanges}/>
                <input type="color" value={color} style={{height: '40px', width: '30px'}} className="bg-transparent"
                       onChange={col => setColor(col.target.value)}
                       onBlur={saveChanges}/>
            </Stack>
            <Button size="small" color="primary" sx={{borderRadius: 10, mt: 0, width: 2}} variant="outlined"
                    onClick={deleteEdgeLabel(props.id)}>
                <RemoveIcon fontSize="inherit"/>
            </Button>
            {props.edgeLabels.length - 1 === props.id &&
                <Button size="small" color="primary" sx={{borderRadius: 10, mt: 0}} variant="outlined"
                        onClick={props.addLabel}>
                    <AddIcon fontSize="inherit"/>
                </Button>
            }
        </div>
    )
}
const MyToolTip = (props) => {
    return (
        <Tooltip title={props.title} arrow placement="right-end" enterDelay={700} leaveDelay={200}>
            {props.children}
        </Tooltip>
    )
}

const SideBar = forwardRef((props, ref) => {
    const [grFile, setGr] = useState(null);
    const [nodeFile, setNodeData] = useState(null);
    const [coFile, setCoFile] = useState(null)
    const [graphType, setGraphType] = useState('');
    const [currentLabel, setCurrentLabel] = useState('');
    const [currentX, setCurrentX] = useState('');
    const [currentY, setCurrentY] = useState('');
    const [socialIsUpload, setSocialIsUpload] = useState(false);
    const [edgeType, setEdgeType] = useState('');
    const [generationModel, setGenerationModel] = useState('');
    const [spatialIsUpload, setSpatialIsUpload] = useState(false);
    const [spatialDistribution, setSpatialDistribution] = useState('');
    const [spatialSeed, setSpatialSeed] = useState('');
    const [spatialGeometry, setSpatialGeometry] = useState('');
    const [numberNodes, setNumberNodes] = useState('');
    const [degree, setDegree] = useState('');
    const [scaleFreeAlpha, setScaleFreeAlpha] = useState('41');
    const [scaleFreeBeta, setScaleFreeBeta] = useState('54');
    const [scaleFreeGamma, setScaleFreeGamma] = useState('5');
    const [scaleFreeDeltaIn, setScaleFreeDeltaIn] = useState(2);
    const [scaleFreeDeltaOut, setScaleFreeDeltaOut] = useState(0);
    const [holmeAndKimProbability, setHolmeAndKimProbability] = useState(0);
    const [generationModelDirected, setGenerationModelDirected] = useState('');
    const [percentagePercentage, setPercentagePercentage] = useState('');
    const [numberSpatialNodes, setNumberSpatialNodes] = useState('');
    const [mean, setMean] = useState('');
    const [std, setStd] = useState('');
    const [currentNodeIsSpatial, setCurrentNodeIsSpatial] = useState('');
    const [currentEdgeIsDirected, setCurrentEdgeIsDirected] = useState('');
    const [diagonalPercentage, setDiagonalPercentage] = useState(0);
    const [diagonalBuffer, setDiagonalBuffer] = useState('');
    const [parcelSrange, setparcelSrange] = useState('');
    const [parcelDither, setParcelDither] = useState('');
    const [bitProbability, setBitProbability] = useState('');
    const [bitDigits, setBitDigits] = useState('');
    const [maxX, setMaxX] = useState('');
    const [maxY, setMaxY] = useState('');
    const [currentWidth, setCurrentWidth] = useState('');
    const [currentHeight, setCurrentHeight] = useState('');
    const [edgeLabels, setEdgeLabels] = useState([]);
    const [openSnackbar, setOpenSnackbar] = useState(false);
    const [alertMessage, setAlertMessage] = useState('');
    const [alertSeverity, setAlertSeverity] = useState('success');
    const [showLargeGraphDialog, setShowLargeGraphDialog] = useState(false);
    const largeWarningThreshold = 100;
    const xScaleRef = useRef(null);
    const yScaleRef = useRef(null);

    function getUserConfig() {
        return {
            graphType: graphType,
            socialIsUpload: socialIsUpload,
            edgeType: edgeType,
            generationModel: generationModel,
            spatialIsUpload: spatialIsUpload,
            spatialDistribution: spatialDistribution,
            spatialSeed: spatialSeed,
            spatialGeometry: spatialGeometry,
            numberNodes: numberNodes,
            degree: degree,
            scaleFreeAlpha: scaleFreeAlpha,
            scaleFreeBeta: scaleFreeBeta,
            scaleFreeGamma: scaleFreeGamma,
            scaleFreeDeltaIn: scaleFreeDeltaIn,
            scaleFreeDeltaOut: scaleFreeDeltaOut,
            holmeAndKimProbability: holmeAndKimProbability,
            generationModelDirected: generationModelDirected,
            percentagePercentage: percentagePercentage,
            numberSpatialNodes: numberSpatialNodes,
            mean: mean,
            std: std,
            diagonalPercentage: diagonalPercentage,
            diagonalBuffer: diagonalBuffer,
            parcelSrange: parcelSrange,
            parcelDither: parcelDither,
            bitProbability: bitProbability,
            bitDigits: bitDigits,
            maxX: maxX,
            maxY: maxY,
        }
    }
    function setUserConfig(config) {
        setEdgeLabels(config.edgeLabels);
        setGraphType(config.graphType);
        setSocialIsUpload(config.socialIsUpload);
        setEdgeType(config.edgeType);
        setGenerationModel(config.generationModel);
        setSpatialIsUpload(config.spatialIsUpload);
        setSpatialDistribution(config.spatialDistribution);
        setSpatialSeed(config.spatialSeed);
        setSpatialGeometry(config.spatialGeometry);
        setNumberNodes(config.numberNodes);
        setDegree(config.degree);
        setScaleFreeAlpha(config.scaleFreeAlpha);
        setScaleFreeBeta(config.scaleFreeBeta);
        setScaleFreeGamma(config.scaleFreeGamma);
        setScaleFreeDeltaIn(config.scaleFreeDeltaIn);
        setScaleFreeDeltaOut(config.scaleFreeDeltaOut);
        setHolmeAndKimProbability(config.holmeAndKimProbability);
        setGenerationModelDirected(config.generationModelDirected);
        setPercentagePercentage(config.percentagePercentage);
        setNumberSpatialNodes(config.numberSpatialNodes);
        setMean(config.mean);
        setStd(config.std);
        setDiagonalPercentage(config.diagonalPercentage);
        setDiagonalBuffer(config.diagonalBuffer);
        setparcelSrange(config.parcelSrange);
        setParcelDither(config.parcelDither);
        setBitProbability(config.bitProbability);
        setBitDigits(config.bitDigits);
        setMaxX(config.maxX);
        setMaxY(config.maxY);
    }
    useImperativeHandle(ref, () => ({
        getUserConfig: getUserConfig,
        setUserConfig: setUserConfig,
    }));
    const validateAllInputs = () => {
        let allValid = true;
        let errorStrings = "";
        if (graphType === '') {
            errorStrings += " \n - Network Type must be selected";
            allValid = false;
        }
        if (!socialIsUpload) {
            if (generationModel === '' && generationModelDirected === '') {
                errorStrings += " \n - Social generation model must be selected";
                allValid = false;
            }
            if (numberNodes === '') {
                errorStrings += " \n - Number of nodes must be filled in";
                allValid = false;
            }
            if (generationModel === "barabasi") {
                if (numberNodes === '' || degree === '') {
                    errorStrings += " \n - Number of nodes and degree must be filled in";
                    allValid = false;
                }
                if (+numberNodes < +degree) {
                    errorStrings += " \n - Number of nodes must be greater than degree";
                    allValid = false;
                }
            }
            if (generationModel === "scale_free") {
                if ((+scaleFreeAlpha) + (+scaleFreeBeta) + (+scaleFreeGamma) !== 100) {

                    errorStrings += " \n - The sum of alpha, beta and gamma must be 100";
                    allValid = false;
                }

            }
            if (generationModel === "power") {
                if (numberNodes === '' || degree === '' || holmeAndKimProbability === '') {
                    errorStrings += " \n - Number of nodes, degree and probability must be filled in";
                    allValid = false;
                }
                if (+numberNodes < +degree) {
                    errorStrings += " \n - Number of nodes must be greater than degree";
                    allValid = false;
                }
                if (+holmeAndKimProbability / 100 > 1 || +holmeAndKimProbability / 100 < 0) {
                    errorStrings += " \n - Probability must be between 0 and 100";
                    allValid = false;
                }
            }
        } else {
            if (!grFile) {
                errorStrings += " \n - Graph file must be uploaded";
                allValid = false;
            }
        }
        if (!spatialIsUpload) {
            if (spatialDistribution === '') {
                errorStrings += " \n - Spatial distribution must be selected";
                allValid = false;
            }
            if (spatialGeometry === '') {
                errorStrings += " \n - Spatial geometry must be selected";
                allValid = false;
            }
            if (spatialDistribution === "diagonal") {
                if (diagonalPercentage === '' || diagonalBuffer === '') {
                    errorStrings += " \n - Diagonal percentage and buffer must be filled in";
                    allValid = false;
                }
            }
            if (spatialDistribution === "bit") {
                if (bitProbability === '' || bitDigits === '') {
                    errorStrings += " \n - Probability and digits must be filled in";
                    allValid = false;
                }
            }
            if (spatialDistribution === "parcel") {
                if (parcelSrange === '' || parcelDither === '') {
                    errorStrings += " \n - Srange and dither must be filled in";
                    allValid = false;
                }
            }
            if (spatialGeometry === 'box') {
                if (maxX === '' || maxY === '') {
                    errorStrings += " \n - Max X and Y must be filled in";
                    allValid = false;
                }
            }
        } else {
            if (!coFile) {
                errorStrings += " \n - Spatial file must be uploaded";
                allValid = false;
            }
        }
        if (graphType === "percentage") {
            if (percentagePercentage === '') {
                errorStrings += " \n - Percentage must be filled in";
                allValid = false;
            }
        }
        if (graphType === "fourSquare") {
            if (numberSpatialNodes === '' || mean === '' || std === '') {
                errorStrings += " \n - Number of nodes, mean and standard deviation must be filled in";
                allValid = false;
            }
        }
        if (graphType === "geoTweet") {
            if (numberSpatialNodes === '' || mean === '' || std === '') {
                errorStrings += " \n - Number of nodes must be filled in";
                allValid = false;
            }
        }
        if (!allValid) {
            setOpenSnackbar(true);
            setAlertMessage(
                errorStrings);
            setAlertSeverity("error");
        }
        return allValid;
    }
    const addLabel = (event) => {
        setEdgeLabels([...edgeLabels, {label: '', percentage: 0, color: null}]);
    }

    const drawPropertiesGraph = () => {
        if (!props.openProperties) return;
        if (!props.selectedObject) return;
        if (typeof props.selectedObject.fixedX !== 'number') return;
        if (props.selectedTab === 'combined') return;
        const zoomer = d3.select('#propertiesZoomer');
        const svg = d3.select('#propertiesSvg');
        svg.call(d3.zoom().on("zoom", (event) => {
            zoomer.attr("transform", event.transform)
        }))
            .on('dblclick.zoom', null);
        let parentDiv = document.getElementById('propertiesSvg');
        if(parentDiv === null) return;
        d3.select("#propertiesForceNodes").selectAll("*").remove();
        d3.select('#propertiesLabels').selectAll("*").remove();
        const width = parentDiv.clientWidth - 50;
        const height = parentDiv.clientHeight - 50;

        if (props.selectedObject?.id && (typeof props.selectedObject?.fixedX === 'number') && props.selectedTab === 'social') {
            let simulationJson = JSON.parse(JSON.stringify(props.jsonData));
            zoomer.attr("transform", d3.zoomIdentity)
            d3.select("#propertiesSvg").call(d3.zoom().transform, d3.zoomIdentity);



            d3.select('#propertiesZoomer')
                .attr('width', width + 'px')
                .attr('height', height + 'px')
                .attr('transform', 'translate(40, 25)');

            simulationJson.nodes = simulationJson.nodes.filter(node => node.fixedX !== undefined && node.fixedY !== undefined && node.id === props.selectedObject.id);
            const padding = 0.2;
            xScaleRef.current = d3.scaleLinear()
                .domain([Math.min(...simulationJson.nodes.map(n => n.fixedX)) - padding, Math.max(...simulationJson.nodes.map(n => Number(n.fixedX ?? 0) + Number(n.width ?? 0))) + padding])
                .range([0, width]);
            console.log(Math.min(...simulationJson.nodes.map(n => n.fixedY)), Math.max(...simulationJson.nodes.map(n => n.fixedY)))
            yScaleRef.current = d3.scaleLinear()
                .domain([Math.min(...simulationJson.nodes.map(n => Number(n.fixedY ?? 0) - Number(n.height ?? 0))) - padding, Math.max(...simulationJson.nodes.map(n => Number(n.fixedY ?? 0))) + padding])
                .range([height, 0]);


            const xAxis = d3.axisBottom(xScaleRef.current)
                .tickSize(-height);
            const yAxis = d3.axisLeft(yScaleRef.current)
                .tickSize(-width);


            svg.select("#propertiesForceNodes").append("g")
                .attr('class', 'grid')
                .attr("transform", `translate(0,${height})`)
                .call(xAxis.ticks(4))
                .style("stroke-dasharray", "1 1")
                .style('stroke-width', '0.5px')
                .selectAll("text")
                .style("font-size", "10px");

            // Add the y-axis to the SVG
            svg.select("#propertiesForceNodes").append("g")
                .attr('class', 'grid')
                .call(yAxis.ticks(4))
                .style("stroke-dasharray", "1 1")
                .style('stroke-width', '0.5px')
                .selectAll("text")
                .style("font-size", "10px");

            const isRect = simulationJson.nodes.some(node => node.width && node.height);
            const node = svg.select('#propertiesForceNodes')
                .selectAll(isRect ? "rect" : "circle")
                .data(simulationJson.nodes)
                .join(isRect ? "rect" : "circle")
                .attr("r", 10)
                .attr("x", d => {
                    return xScaleRef.current(d.fixedX)
                })
                .attr("y", d => yScaleRef.current(d.fixedY))
                .attr("width", d => {
                    return xScaleRef.current(Number(d.fixedX) + Number(d.width)) - xScaleRef.current(d.fixedX)
                })
                .attr("height", d => yScaleRef.current(d.fixedY) - yScaleRef.current(Number(d.fixedY) + Number(d.height)))
                .attr("stroke", d => d.id === props.selectedObject?.id ? props.highlightNodeColor : props.standardNodeColor)
                .attr("cx", d => {
                    return xScaleRef.current(d.fixedX)

                })
                .attr("cy", d => {
                    return yScaleRef.current(d.fixedY)
                })
                .attr("fill", props.standardNodeColor)
                .style("stroke-width", 2)

            const maxFontSize = 12;
            const minFontSize = 4;
            const maxLength = 10;
            const nodeText = svg.select('#propertiesLabels')
                .selectAll("text")
                .data(simulationJson.nodes)
                .join("text")
                .text(d => d.label ?? d.id)
                .attr("dx", 0)
                .attr('id', d => `text-${d.id}`)
                .attr("dy", "0.35em")
                .attr("x", d => {
                    if (!d.width) {
                        return xScaleRef.current(d.fixedX)
                    } else {
                        return xScaleRef.current(Number(d.fixedX) + d.width / 2)
                    }
                })
                .attr("y", d => {
                    if (!d.height) {
                        return yScaleRef.current(d.fixedY)
                    } else {
                        return yScaleRef.current(Number(d.fixedY) - d.height / 2)
                    }
                })
                .style("text-anchor", "middle")
                .style('fill', "darkOrange")
                .style('z-index', 100)
                .style('pointer-events', 'none')
                .style('font-size', d => {
                    if (!d.label) {
                        return maxFontSize + 'px';
                    } else {
                        const length = d.label.length;
                        const fontSize = maxFontSize - ((length / maxLength) * (maxFontSize - minFontSize));
                        return `${Math.max(fontSize, minFontSize)}px`;
                    }
                });

        } else if (props.selectedObject?.id && (typeof props.selectedObject?.fixedX === 'number') && props.selectedTab === 'spatial') {
            let relevantLinks = props.jsonData.links.filter(link => {
                return (link.source === props.selectedObject.id || link.target === props.selectedObject.id)
            });
            let relevantNodes = props.jsonData.nodes.filter(node => {
                return relevantLinks.find(link => link.source === node.id || link.target === node.id)
            });
            if(relevantNodes.length === 0) {
                relevantNodes = props.jsonData.nodes.filter(node => node.id === props.selectedObject.id);
            }
            const simulationJson = {
                nodes: JSON.parse(JSON.stringify(relevantNodes)),
                links: JSON.parse(JSON.stringify(relevantLinks))
            }


            const links = svg.select('#propertiesForceNodes')
                .selectAll('line')
                .data(simulationJson.links)
                .join('line')
                .style('stroke', d => props.standardEdgeColor)
                .style('stroke-width', 2)

            const node = svg.select('#propertiesForceNodes')
                .selectAll("circle")
                .data(simulationJson.nodes)
                .join("circle")
                .attr("r", 10)
                .attr("stroke", d => d.id === props.selectedObject?.id ? props.highlightNodeColor : props.standardNodeColor)
                .style("stroke-width", 0.5)

            const maxFontSize = 10;
            const minFontSize = 5;
            const maxLength = 5;
            const nodeText = svg.select('#propertiesForceNodes')
                .selectAll("text")
                .data(simulationJson.nodes)
                .join("text")
                .text(d => d.label ?? d.id)
                .attr("dx", 0)
                .attr("dy", "0.35em")
                .style("text-anchor", "middle")
                .style('fill', "darkOrange")
                .style('z-index', 100)
                .style('pointer-events', 'none')
                .style('font-size', d => {
                    if (!d.label) {
                        return maxFontSize + 'px';
                    } else {
                        const length = d.label.length;
                        const fontSize = maxFontSize - ((length / maxLength) * (maxFontSize - minFontSize));
                        return `${Math.max(fontSize, minFontSize)}px`;
                    }
                });

            const propertiesSimulation = d3.forceSimulation(simulationJson.nodes)
                .velocityDecay(0.7)
                .force("center", d3.forceCenter().x(width / 2).y(height / 2)) // Attraction to the center of the svg area
                .force("collide", d3.forceCollide().strength(0.5).radius(20).iterations(1)) // Force that avoids circle overlapping
                .force("link", d3.forceLink()
                    .id((d) => {
                        return d.id;
                    })
                    .links(simulationJson.links))

            propertiesSimulation
                .nodes(simulationJson.nodes)
                .on("tick", function (tick) {
                        node
                            .attr('cx', d => d.x)
                            .attr('cy', d => d.y)
                        links
                            .attr("x1", function (d) {
                                return d.source.x;
                            })
                            .attr("y1", function (d) {
                                return d.source.y;
                            })
                            .attr("x2", function (d) {
                                return d.target.x;
                            })
                            .attr("y2", function (d) {
                                return d.target.y;
                            });
                        nodeText
                            .attr("x", function (d) {
                                return d.x;
                            })
                            .attr("y", function (d) {
                                return d.y;
                            });

                        if (propertiesSimulation.alpha() < 0.02) {
                            propertiesSimulation.stop();
                        }
                    }
                );
            propertiesSimulation.restart();

        }
    }
    useEffect(() => {
        drawPropertiesGraph();
    }, [props.standardNodeColor, props.standardEdgeColor]);

    useEffect(() => {
        setCurrentLabel(props.selectedObject?.label ?? '');
        setCurrentX(typeof  props.selectedObject?.fixedX !== 'number' ? '' : props.selectedObject?.fixedX);
        setCurrentY(typeof  props.selectedObject?.fixedY !== 'number' ? '' : props.selectedObject?.fixedY);
        setCurrentWidth(props.selectedObject?.width ?? '');
        setCurrentHeight(props.selectedObject?.height ?? '');
        debugger
        const nodeIsSpatial = typeof props.selectedObject?.fixedX === 'number';
        console.log(nodeIsSpatial)
        setCurrentEdgeIsDirected(!!props.selectedObject?.directed);
        setCurrentNodeIsSpatial(nodeIsSpatial);
        drawPropertiesGraph();
    }, [props.selectedObject, props.selectedTab]);

    const changeObjectData = () => {
        debugger
        props.setFutureStack([]);
        const oldJson = JSON.parse(JSON.stringify(props.jsonData));
        props.setHistoryStack(prevHistoryStack => [...prevHistoryStack, oldJson]);
        if (props.selectedObject) {
            props.setJson(prevJsonData => ({
                ...prevJsonData,
                nodes: prevJsonData.nodes.map(node =>
                    node.id === props.selectedObject.id
                        ? {
                            ...node, label: currentLabel === '' ? undefined : currentLabel,
                            fixedX: currentX !== '' ? (currentX === undefined ? undefined: +(currentX) ): node.fixedX,
                            fixedY: currentY !== '' ? (currentY === undefined ? undefined: +(currentY) ): node.fixedY,
                            width: currentWidth !== '' ? currentWidth : node.width,
                            height: currentHeight !== '' ? currentHeight : node.height,
                        }
                        : node
                ),
                links: prevJsonData.links.map((link) => {
                        if (link?.source === (props.selectedObject?.source?.id ?? props.selectedObject?.source) &&
                            link?.target === (props.selectedObject?.target?.id ?? props.selectedObject?.target)) {
                            return {...link, label: currentLabel, directed: currentEdgeIsDirected}
                        }
                        return link
                    }
                )
            }));
            props.setUnsavedChanges(true);
        }
    }

    const handleTypeChange = (isSpatial) => {
        const isRect = props.jsonData.nodes.some(node => node.width && node.height);
        setCurrentNodeIsSpatial(isSpatial);
        if (isSpatial) {
            setCurrentX('0');
            setCurrentY('0');
            if (isRect) {
                setCurrentWidth(0);
                setCurrentHeight(0);
            }
        } else {
            setCurrentX(undefined);
            setCurrentY(undefined);
            setCurrentWidth('');
            setCurrentHeight('');
        }
    }

    const cancelChanges = () => {
        setCurrentLabel(props.selectedObject?.label ?? '');
        setCurrentX(props.selectedObject?.fixedX ?? '');
        setCurrentY(props.selectedObject?.fixedY ?? '');
        setCurrentWidth(props.selectedObject?.width ?? '');
        setCurrentHeight(props.selectedObject?.height ?? '');
        const nodeIsSpatial = !!props.selectedObject?.fixedX;
        setCurrentNodeIsSpatial(nodeIsSpatial);
        setCurrentEdgeIsDirected(!!props.selectedObject?.directed);
    }

    const deleteSelectedObject = () => {
        props.setFutureStack([]);
        const currentJson = JSON.parse(JSON.stringify(props.jsonData));
        props.setHistoryStack(prevHistoryStack => [...prevHistoryStack, currentJson]);
        if (props.selectedObject) {
            props.setJson(prevJsonData => ({
                ...prevJsonData,
                nodes: prevJsonData.nodes.filter(node => node.id !== props.selectedObject.id),
                links: prevJsonData.links.filter(link => {
                    if (props.selectedObject?.id) {
                        return (link.source !== props.selectedObject.id && link.target !== props.selectedObject.id)
                    } else {
                        debugger
                        return (link.source !== (props.selectedObject?.source?.id ?? props.selectedObject?.source))
                            || (link.target !== (props.selectedObject?.target?.id ?? props.selectedObject?.target))
                    }
                })
            }))
            props.setUnsavedChanges(true);
        }
    }
    return (
        <ThemeProvider theme={props.theme}>
            <LargeGraphDialog
                openCondition={showLargeGraphDialog}
                setOpenCondition={setShowLargeGraphDialog}
                drawGraph={props.drawGraph}
                setDrawGraph={props.setDrawGraph}
                handleExport={async e => {
                    const downloadFiles = await props.handleExport();
                    debugger
                    const gr = downloadFiles[0];
                    const co = downloadFiles[1];
                    await props.download('project.gr', gr, '.gr');
                    await props.download('project.co', co, '.co')
                        .then(response => {
                            setAlertMessage('Project successfully exported');
                            setAlertSeverity('success');
                            setOpenSnackbar(true);
                        })
                        .catch(error => {
                            setAlertMessage('Project could not be exported');
                            setAlertSeverity('error');
                            setOpenSnackbar(true);
                        })
                    setShowLargeGraphDialog(false);
                }
                }
            >
                You are trying to generate a large, which could take a while.
                Do you really want to display the graph, or just export it ?
            </LargeGraphDialog>
            <Snackbar open={openSnackbar} anchorOrigin={{vertical: 'bottom', horizontal: 'right'}}
                      autoHideDuration={10000} onClose={e => setOpenSnackbar(false)}>
                <Alert
                    onClose={e => setOpenSnackbar(false)}
                    severity={alertSeverity}
                    variant="filled"
                    sx={{width: '100%'}}
                >
                    {alertSeverity === "error" &&
                        <div>
                        <div>Graph could not be generated, please fix the following:</div>
                        {
                            alertMessage.split('\n').map((str, index) => {
                                return <div style={{marginTop: '5px'}} key={index}>{str}</div>
                            })
                        }
                    </div>
                    }
                    {
                        alertSeverity === "success" && alertMessage
                    }
                </Alert>
            </Snackbar>
            <div
                className="w-1/5 bg-neutral-100 dark:bg-neutral-700 h-screen border-r-2 border-neutral-900 flex flex-col justify-between items-center">
                <div className="overflow-auto px-4 w-full mb-2 flex-grow">
                    <FormControl sx={{mt: 2}} fullWidth>
                        <InputLabel id="demo-simple-select-label">Network Type</InputLabel>
                        <Select
                            labelId="demo-simple-select-label"
                            id="demo-simple-select"
                            value={graphType}
                            label="Network Type"
                            onChange={e => setGraphType(e.target.value)}
                        >
                            <MenuItem value={"percentage"}>G_s</MenuItem>
                            <MenuItem value={"fourSquare"}>G_c</MenuItem>
                            <MenuItem value={"geoTweet"}>G_p</MenuItem>
                        </Select>
                    </FormControl>
                    <Collapsable title="Social">
                        <FormControl fullWidth>
                            <Stack direction="row" spacing={1} alignItems="center">
                                <Typography sx={{ color: 'textColor.main' }}>Generate</Typography>
                                <Switch checked={socialIsUpload} onChange={e => setSocialIsUpload(e.target.checked)}/>
                                <Typography sx={{ color: 'textColor.main' }}>Upload</Typography>
                            </Stack>
                            {!socialIsUpload &&

                                <div>
                                    <FormControl sx={{mt: 2}} fullWidth size="small">
                                        <InputLabel id="graph-type-select-label">Edge Type</InputLabel>
                                        <Select
                                            labelId="graph-type-select-label"
                                            id="demo-simple-select"
                                            value={edgeType}
                                            label="Edge Type"
                                            onChange={e => setEdgeType(e.target.value)}
                                        >
                                            <MenuItem value={"undirected"}>Undirected</MenuItem>
                                            <MenuItem value={"directed"}>Directed</MenuItem>
                                        </Select>
                                    </FormControl>
                                    {edgeType === "undirected" &&
                                        <div>
                                            <FormControl sx={{mt: 2}} fullWidth size="small">
                                                <InputLabel id="graph-generation-model-label">Generation
                                                    model</InputLabel>
                                                <Select
                                                    labelId="generation-model-select-label"
                                                    id="graph-generation-model"
                                                    value={generationModel}
                                                    label="Generation model"
                                                    onChange={e => setGenerationModel(e.target.value)}
                                                >
                                                    <MenuItem value={"barabasi"}>Barabasi</MenuItem>
                                                    <MenuItem value={"scale_free"}>Scale Free</MenuItem>
                                                    <MenuItem value={"power"}>Holme and Kim</MenuItem>
                                                </Select>
                                            </FormControl>
                                            {
                                                generationModel === "barabasi" &&
                                                <div className="my-3">
                                                    <MyToolTip title="Number of nodes to be generated">
                                                        <TextField id="outlined-basic" sx={{my: 1}}
                                                                   label="Number of Nodes"
                                                                   inputProps={{
                                                                       pattern: '[0-9]*', // Only allow digits
                                                                       inputMode: 'numeric',
                                                                   }}
                                                                   variant="outlined"
                                                                   size="small"
                                                                   value={numberNodes}
                                                                   onChange={e => validateInputsRegex(e, setNumberNodes, /^[1-9]\d*$/)}/>
                                                    </MyToolTip>
                                                    <MyToolTip
                                                        title={"Number of edges to attach from a new node to existing nodes"}>
                                                        <TextField id="outlined-basic" sx={{my: 1}} label="Degree"
                                                                   variant="outlined"
                                                                   size="small"
                                                                   value={degree}
                                                                   onChange={e => validateInputsRegex(e, setDegree, /^[1-9]\d*$/)}/>
                                                    </MyToolTip>
                                                </div>
                                            }
                                            {
                                                generationModel === "scale_free" &&
                                                <div className="my-3">
                                                    <MyToolTip title={"Number of nodes to be generated"}>
                                                        <TextField id="outlined-basic" sx={{my: 1}}
                                                                   label="Number of Nodes"
                                                                   variant="outlined"
                                                                   size="small"
                                                                   value={numberNodes}

                                                                   onChange={e => validateInputsRegex(e, setNumberNodes, /^[1-9]\d*$/)}/>
                                                    </MyToolTip>

                                                    <MyToolTip
                                                        title={"Probability for adding a new node connected to an existing node chosen randomly according to the in-degree distribution."}>
                                                        <TextField id="outlined-basic" sx={{my: 1}} label="Alpha"
                                                                   variant="outlined"
                                                                   size="small"
                                                                   value={scaleFreeAlpha}
                                                                   InputProps={{
                                                                       endAdornment: <PercentIcon fontSize="small"/>,
                                                                   }}
                                                                   onChange={e => validateInputsRegex(e, setScaleFreeAlpha, /^(0|[1-9][0-9]?|100)$/)}/>
                                                    </MyToolTip>
                                                    <MyToolTip
                                                        title="Probability for adding an edge between two existing nodes. One existing node is chosen randomly according the in-degree distribution and the other chosen randomly according to the out-degree distribution.">
                                                        <TextField id="outlined-basic" sx={{my: 1}} label="Beta"
                                                                   variant="outlined"
                                                                   size="small"
                                                                   value={scaleFreeBeta}
                                                                   InputProps={{
                                                                       endAdornment: <PercentIcon fontSize="small"/>,
                                                                   }}
                                                                   onChange={e => validateInputsRegex(e, setScaleFreeBeta, /^(0|[1-9][0-9]?|100)$/)}/>
                                                    </MyToolTip>
                                                    <MyToolTip
                                                        title={"Probability for adding a new node connected to an existing node chosen randomly according to the out-degree distribution."}>
                                                        <TextField id="outlined-basic" sx={{my: 1}} label="Gemma"
                                                                   variant="outlined"
                                                                   size="small"
                                                                   value={scaleFreeGamma} InputProps={{
                                                            endAdornment: <PercentIcon fontSize="small"/>,
                                                        }}
                                                                   onChange={e => validateInputsRegex(e, setScaleFreeGamma, /^(0|[1-9][0-9]?|100)$/)}/>
                                                    </MyToolTip>
                                                    <MyToolTip
                                                        title={"Bias for choosing nodes from in-degree distribution."}>
                                                        <TextField id="outlined-basic" sx={{my: 1}} label="Delta In"
                                                                   variant="outlined"
                                                                   size="small"
                                                                   value={scaleFreeDeltaIn}
                                                                   InputProps={{
                                                                       endAdornment: <PercentIcon fontSize="small"/>,
                                                                   }}
                                                                   onChange={e => validateInputsRegex(e, setScaleFreeDeltaIn, /^(0|[1-9][0-9]?|100)$/)}/>
                                                    </MyToolTip>
                                                    <MyToolTip
                                                        title={"Bias for choosing nodes from out-degree distribution."}>
                                                        <TextField id="outlined-basic" sx={{my: 1}} label="Delta Out"
                                                                   variant="outlined"
                                                                   size="small"
                                                                   value={scaleFreeDeltaOut}
                                                                   InputProps={{
                                                                       endAdornment: <PercentIcon fontSize="small"/>,
                                                                   }}
                                                                   onChange={e => validateInputsRegex(e, setScaleFreeDeltaOut, /^(0|[1-9][0-9]?|100)$/)}/>
                                                    </MyToolTip>
                                                </div>
                                            }
                                            {
                                                generationModel === "power" &&
                                                <div className="my-3">
                                                    <MyToolTip title={"Number of nodes to be generated"}>
                                                        <TextField id="outlined-basic" sx={{my: 1}}
                                                                   label="Number of Nodes"
                                                                   variant="outlined"
                                                                   size="small"
                                                                   value={numberNodes}
                                                                   onChange={e => setNumberNodes(e.target.value)}/>
                                                    </MyToolTip>
                                                    <MyToolTip
                                                        title={"The number of random edges to add for each new node"}>
                                                        <TextField id="outlined-basic" sx={{my: 1}} label="Degree"
                                                                   variant="outlined"
                                                                   size="small"
                                                                   value={degree}
                                                                   onChange={e => setDegree(e.target.value)}/>
                                                    </MyToolTip>
                                                    <MyToolTip
                                                        title={"Probability of adding a triangle after adding a random edge"}>
                                                        <TextField id="outlined-basic" sx={{my: 1}} label="Probability"
                                                                   variant="outlined"
                                                                   size="small"
                                                                   InputProps={{
                                                                       endAdornment: <PercentIcon fontSize="small"/>,
                                                                   }}
                                                                   value={holmeAndKimProbability}
                                                                   onChange={e => validateInputsRegex(e, setHolmeAndKimProbability, /^(0|[1-9][0-9]?|100)$/)}/>
                                                    </MyToolTip>
                                                </div>
                                            }
                                        </div>
                                    }
                                    {edgeType === "directed" &&
                                        <div>
                                            <FormControl sx={{mt: 2}} fullWidth size="small">
                                                <InputLabel id="graph-generation-model-label">Generation
                                                    model</InputLabel>
                                                <Select
                                                    labelId="generation-model-select-label"
                                                    id="graph-generation-model"
                                                    value={generationModelDirected}
                                                    label="Generation model"
                                                    onChange={e => setGenerationModelDirected(e.target.value)}
                                                >
                                                    <MenuItem value={"scale_free_digraph"}>Scale Free</MenuItem>
                                                </Select>
                                            </FormControl>
                                            {
                                                generationModelDirected === "scale_free_digraph" &&
                                                <div className="my-3">
                                                    <MyToolTip title={"Number of nodes to be generated"}>
                                                        <TextField id="outlined-basic" sx={{my: 1}}
                                                                   label="Number of Nodes"
                                                                   variant="outlined"
                                                                   size="small"
                                                                   value={numberNodes}

                                                                   onChange={e => validateInputsRegex(e, setNumberNodes, /^[1-9]\d*$/)}/>
                                                    </MyToolTip>

                                                    <MyToolTip
                                                        title={"Probability for adding a new node connected to an existing node chosen randomly according to the in-degree distribution."}>
                                                        <TextField id="outlined-basic" sx={{my: 1}} label="Alpha"
                                                                   variant="outlined"
                                                                   size="small"
                                                                   value={scaleFreeAlpha}
                                                                   InputProps={{
                                                                       endAdornment: <PercentIcon fontSize="small"/>,
                                                                   }}
                                                                   onChange={e => validateInputsRegex(e, setScaleFreeAlpha, /^(0|[1-9][0-9]?|100)$/)}/>
                                                    </MyToolTip>
                                                    <MyToolTip
                                                        title="Probability for adding an edge between two existing nodes. One existing node is chosen randomly according the in-degree distribution and the other chosen randomly according to the out-degree distribution.">
                                                        <TextField id="outlined-basic" sx={{my: 1}} label="Beta"
                                                                   variant="outlined"
                                                                   size="small"
                                                                   value={scaleFreeBeta}
                                                                   InputProps={{
                                                                       endAdornment: <PercentIcon fontSize="small"/>,
                                                                   }}
                                                                   onChange={e => validateInputsRegex(e, setScaleFreeBeta, /^(0|[1-9][0-9]?|100)$/)}/>
                                                    </MyToolTip>
                                                    <MyToolTip
                                                        title={"Probability for adding a new node connected to an existing node chosen randomly according to the out-degree distribution."}>
                                                        <TextField id="outlined-basic" sx={{my: 1}} label="Gemma"
                                                                   variant="outlined"
                                                                   size="small"
                                                                   value={scaleFreeGamma} InputProps={{
                                                            endAdornment: <PercentIcon fontSize="small"/>,
                                                        }}
                                                                   onChange={e => validateInputsRegex(e, setScaleFreeGamma, /^(0|[1-9][0-9]?|100)$/)}/>
                                                    </MyToolTip>
                                                    <MyToolTip
                                                        title={"Bias for choosing nodes from in-degree distribution."}>
                                                        <TextField id="outlined-basic" sx={{my: 1}} label="Delta In"
                                                                   variant="outlined"
                                                                   size="small"
                                                                   value={scaleFreeDeltaIn}
                                                                   InputProps={{
                                                                       endAdornment: <PercentIcon fontSize="small"/>,
                                                                   }}
                                                                   onChange={e => validateInputsRegex(e, setScaleFreeDeltaIn, /^(0|[1-9][0-9]?|100)$/)}/>
                                                    </MyToolTip>
                                                    <MyToolTip
                                                        title={"Bias for choosing nodes from out-degree distribution."}>
                                                        <TextField id="outlined-basic" sx={{my: 1}} label="Delta Out"
                                                                   variant="outlined"
                                                                   size="small"
                                                                   value={scaleFreeDeltaOut}
                                                                   InputProps={{
                                                                       endAdornment: <PercentIcon fontSize="small"/>,
                                                                   }}
                                                                   onChange={e => validateInputsRegex(e, setScaleFreeDeltaOut, /^(0|[1-9][0-9]?|100)$/)}/>
                                                    </MyToolTip>
                                                </div>
                                            }
                                        </div>
                                    }
                                    {
                                        edgeLabels.length === 0 &&
                                        <Button sx={{my: 2}} type="submit" variant="contained" color="primary"
                                                startIcon={<AddIcon/>} onClick={addLabel} size="small">
                                            Add custom edge labels
                                        </Button>
                                    }
                                    {
                                        edgeLabels.length > 0 &&
                                        <div>
                                            {
                                                edgeLabels.map((obj, index) => {
                                                    return (
                                                        <EdgeLabelPercentage key={index} id={index}
                                                                             setEdgeLabels={setEdgeLabels}
                                                                             edgeLabels={edgeLabels}
                                                                             addLabel={addLabel}/>
                                                    )
                                                })
                                            }

                                        </div>
                                    }
                                </div>
                            }


                            {socialIsUpload &&
                                <div className="my-2">
                                    <Button component="label" fullWidth="true" variant="contained"
                                            color="primary"
                                            id="coFile"
                                            startIcon={<CloudUploadIcon/>}
                                    >
                                        Upload
                                        <input
                                            type="file"
                                            hidden
                                            onChange={e => {
                                                setGr(e.target.files[0])
                                            }}
                                        />
                                    </Button>
                                </div>
                            }

                        </FormControl>
                    </Collapsable>
                    <Collapsable title="Spatial">
                        <FormControl  fullWidth>
                            <Stack direction="row" spacing={1} alignItems="center">
                                <Typography sx={{ color: 'textColor.main' }}>Generate</Typography>
                                <Switch checked={spatialIsUpload} onChange={e => setSpatialIsUpload(e.target.checked)}/>
                                <Typography sx={{ color: 'textColor.main' }}>Upload</Typography>
                            </Stack>
                            {!spatialIsUpload &&

                                <div>
                                    <FormControl sx={{mt: 2}} fullWidth size="small">
                                        <InputLabel id="spatial-distribution-label">Distribution</InputLabel>
                                        <Select
                                            labelId="spatial-distribution-label"
                                            id="spatial-distribution"
                                            value={spatialDistribution}
                                            label="Distribution"
                                            onChange={e => setSpatialDistribution(e.target.value)}
                                        >
                                            <MenuItem value={"uniform"}>Uniform</MenuItem>
                                            <MenuItem value={"diagonal"}>Diagonal</MenuItem>
                                            <MenuItem value={"gaussian"}>Gaussian</MenuItem>
                                            <MenuItem value={"sierpinski"}>Sierpinski</MenuItem>
                                            <MenuItem value={"bit"}>Bit</MenuItem>
                                            <MenuItem value={"parcel"}>Parcel</MenuItem>
                                        </Select>
                                    </FormControl>
                                    {spatialDistribution === "diagonal" &&
                                        <div>
                                            <MyToolTip title="Percentage of records that are perfectly on the diagonal">
                                                <TextField id="outlined-basic" sx={{mt: 1}} label="Percentage"
                                                           variant="outlined"
                                                           size="small"
                                                           fullWidth="true"
                                                           value={diagonalPercentage} InputProps={{
                                                    endAdornment: <PercentIcon fontSize="small"/>,
                                                }}
                                                           onChange={e => validateInputsRegex(e, setDiagonalPercentage, /^(0|[1-9][0-9]?|100)$/)}/>
                                            </MyToolTip>
                                            <MyToolTip
                                                title="Buffer around the diagonal, that additional points can be in">
                                                <TextField id="outlined-basic" sx={{mt: 1}} label="Buffer"
                                                           variant="outlined"
                                                           size="small"
                                                           fullWidth="true"
                                                           value={diagonalBuffer}
                                                           onChange={e => setDiagonalBuffer(e.target.value)}/>
                                            </MyToolTip>
                                        </div>
                                    }
                                    {spatialDistribution === "parcel" &&
                                        <div>
                                            <MyToolTip title="The split range [0.0, 1.0]">
                                                <TextField id="outlined-basic" sx={{mt: 1}} label="Split Range"
                                                           variant="outlined"
                                                           size="small"
                                                           fullWidth="true"
                                                           value={parcelSrange}
                                                           onChange={e => validateInputsRegex(e, setparcelSrange, /((0(\.[0-9]*)?)|(1(\.0)?))/)}/>
                                            </MyToolTip>
                                            <MyToolTip
                                                title="The amound of noise added to each record as a perctange of its initial size">
                                                <TextField id="outlined-basic" sx={{mt: 1}} label="Dither"
                                                           variant="outlined"
                                                           size="small"
                                                           fullWidth="true"
                                                           value={parcelDither}
                                                           InputProps={{
                                                               endAdornment: <PercentIcon fontSize="small"/>,
                                                           }}
                                                           onChange={e => validateInputsRegex(e, setParcelDither, /^(0|[1-9][0-9]?|100)$/)}/>

                                            </MyToolTip>
                                        </div>
                                    }
                                    {spatialDistribution === "bit" &&
                                        <div>
                                            <MyToolTip title="The probability of a bit being 1">
                                                <TextField id="outlined-basic" sx={{mt: 1}} label="Probability"
                                                           variant="outlined"
                                                           size="small"
                                                           fullWidth="true"
                                                           value={bitProbability}
                                                           InputProps={{
                                                               endAdornment: <PercentIcon fontSize="small"/>,
                                                           }}
                                                           onChange={e => validateInputsRegex(e, setBitProbability, /^(0|[1-9][0-9]?|100)$/)}/>

                                            </MyToolTip>
                                            <MyToolTip title="The number of bits in the bitstring">
                                                <TextField id="outlined-basic" sx={{mt: 1}} label="Digits"
                                                           variant="outlined"
                                                           size="small"
                                                           fullWidth="true"
                                                           value={bitDigits}
                                                           onChange={e => validateInputsRegex(e, setBitDigits, /^(0|[1-9][0-9]?|100)$/)}/>
                                            </MyToolTip>
                                        </div>
                                    }
                                    {spatialDistribution !== "parcel" &&
                                        <FormControl sx={{mt: 2}} fullWidth size="small">
                                            <InputLabel id="spatial-geometry-label">Geometry</InputLabel>
                                            <Select
                                                labelId="spatial-geometry-label"
                                                id="spatial-geometry"
                                                value={spatialGeometry}
                                                label="Geometry"
                                                onChange={e => setSpatialGeometry(e.target.value)}
                                            >
                                                <MenuItem value={"point"}>Point</MenuItem>
                                                <MenuItem value={"box"}>Box</MenuItem>
                                            </Select>
                                        </FormControl>
                                    }
                                    {
                                        spatialGeometry === "box" &&
                                        <div>
                                            <MyToolTip title="Maximum size along the X-axis">
                                                <TextField id="outlined-basic" sx={{mt: 1}} label="Max X"
                                                           variant="outlined"
                                                           size="small"
                                                           fullWidth="true"
                                                           value={maxX}
                                                           onChange={e => validateInputsRegex(e, setMaxX, /^[+-]?([0-9]+([.][0-9]*)?|[.][0-9]+)$/)}/>
                                            </MyToolTip>
                                            <MyToolTip title="Maximum size along the Y-axis">
                                                <TextField id="outlined-basic" sx={{mt: 1}} label="Max Y"
                                                           variant="outlined"
                                                           size="small"
                                                           fullWidth="true"
                                                           value={maxY}
                                                           onChange={e => validateInputsRegex(e, setMaxY, /^[+-]?([0-9]+([.][0-9]*)?|[.][0-9]+)$/)}/>
                                            </MyToolTip>

                                        </div>
                                    }
                                </div>
                            }
                            {spatialIsUpload &&
                                <div className="my-2">
                                    <Button component="label" fullWidth="true" variant="contained" color="primary"
                                            id="coFile"
                                            startIcon={<CloudUploadIcon/>}
                                    >
                                        Upload
                                        <input
                                            type="file"
                                            hidden
                                            onChange={e => {
                                                setCoFile(e.target.files[0])
                                            }}
                                        />
                                    </Button>
                                </div>
                            }
                        </FormControl>
                    </Collapsable>
                    <Collapsable title="Combiner">
                        <FormControl fullwidth="true" size="small">
                            {graphType === "percentage" &&
                                <MyToolTip title="Percentage of Node receiving spatial data">
                                    <TextField id="outlined-basic" label="Percentage" variant="outlined"
                                               size="small"
                                               fullWidth="true"
                                               value={percentagePercentage}
                                               InputProps={{
                                                   endAdornment: <PercentIcon fontSize="small"/>,
                                               }}
                                               onChange={e => validateInputsRegex(e, setPercentagePercentage, /^(0|[1-9][0-9]?|100)$/)}/>

                                </MyToolTip>

                            }
                            {
                                graphType === "geoTweet" &&
                                <div>
                                    <MyToolTip title={"Number of spatial nodes to be generated"}>
                                        <TextField id="outlined-basic" label="Number of spatial Nodes"
                                                   variant="outlined"
                                                   size="small"
                                                   fullWidth="true"
                                                   value={numberSpatialNodes}
                                                   onChange={e => validateInputsRegex(e, setNumberSpatialNodes, /^[1-9]\d*$/)}/>
                                    </MyToolTip>
                                    <MyToolTip title={"Mean number of spatial neighbours for each node"}>
                                        <TextField id="outlined-basic" sx={{mt: 2}} label="Mean" variant="outlined"
                                                   size="small"
                                                   fullWidth="true"
                                                   value={mean}
                                                   onChange={e => validateInputsRegex(e, setMean, /^[+-]?([0-9]+([.][0-9]*)?|[.][0-9]+)$/)}/>
                                    </MyToolTip>
                                    <MyToolTip title={"Standard deviation of spatial neighbours"}>
                                        <TextField id="outlined-basic" sx={{mt: 2}} label="Standard deviation"
                                                   variant="outlined"
                                                   size="small"
                                                   fullWidth="true"
                                                   value={std}
                                                   onChange={e => validateInputsRegex(e, setStd, /^[+-]?([0-9]+([.][0-9]*)?|[.][0-9]+)$/)}/>
                                    </MyToolTip>
                                </div>
                            }
                            {
                                graphType === "fourSquare" &&
                                <div>

                                    <MyToolTip title={"Number of spatial nodes to be generated"}>
                                        <TextField id="outlined-basic" label="Number of spatial nodes"
                                                   variant="outlined"
                                                   size="small"
                                                   fullWidth="true"
                                                   value={numberSpatialNodes}
                                                   onChange={e => validateInputsRegex(e, setNumberSpatialNodes, /^[1-9]\d*$/)}/>
                                    </MyToolTip>
                                    <MyToolTip title={"Mean number of edges to be added to new spatial nodes"}>
                                        <TextField id="outlined-basic" sx={{mt: 2}} label="Mean" variant="outlined"
                                                   size="small"
                                                   fullWidth="true"
                                                   value={mean}
                                                   onChange={e => validateInputsRegex(e, setMean, /^[+-]?([0-9]+([.][0-9]*)?|[.][0-9]+)$/)}/>

                                    </MyToolTip>
                                    <MyToolTip title={"Standard deviation of edges to be added to new spatial nodes"}>
                                        <TextField id="outlined-basic" sx={{mt: 2}} label="Standard deviation"
                                                   variant="outlined"
                                                   size="small"
                                                   fullWidth="true"
                                                   value={std}
                                                   onChange={e => validateInputsRegex(e, setStd, /^[+-]?([0-9]+([.][0-9]*)?|[.][0-9]+)$/)}/>

                                    </MyToolTip>
                                </div>
                            }
                            {
                                graphType === "" &&
                                <div>
                                    <Typography sx={{ color: 'textColor.main' }}>Select a network type</Typography>
                                </div>
                            }
                        </FormControl>
                    </Collapsable>
                    <Button component="label" fullWidth="true" variant="contained" color="secondary" id="coFile"
                            sx={{mb: '3rem'}}
                            startIcon={<HubIcon/>}
                            onClick={async () => {
                                if (!validateAllInputs()) {
                                    return;
                                }
                                if (numberNodes > largeWarningThreshold) {
                                    setShowLargeGraphDialog(true);
                                    props.setDrawGraph(false);
                                }
                                else {
                                    props.setDrawGraph(true);
                                }
                                d3.select('#forceNodes').selectAll("*").remove();
                                props.setJson({nodes: [], links: []});
                                props.setIsLoading(true);
                                if (socialIsUpload) {
                                    const formData = new FormData();
                                    formData.append('grFile', grFile);
                                    await fetch(`${getCurrentUrl()}:5000/upload_gr`, {
                                        method: 'POST',
                                        body: formData
                                    });
                                } else {
                                    await fetch(`${getCurrentUrl()}:5000/generate_social`, {
                                        method: 'POST',
                                        headers: {
                                            'Content-Type': 'application/json',
                                        },
                                        body: JSON.stringify({
                                            gr_type: edgeType === "directed" ? generationModelDirected : generationModel,
                                            number_of_nodes: numberNodes,
                                            degree: degree,
                                            alpha: scaleFreeAlpha / 100,
                                            beta: scaleFreeBeta / 100,
                                            gamma: scaleFreeGamma / 100,
                                            delta_in: scaleFreeDeltaIn / 100,
                                            delta_out: scaleFreeDeltaOut / 100,
                                            probability: holmeAndKimProbability / 100,
                                        }),
                                    })
                                }
                                if (spatialIsUpload) {
                                    const formData = new FormData();
                                    formData.append('coFile', coFile);
                                    await fetch(`${getCurrentUrl()}:5000/upload_co`, {
                                        method: 'POST',
                                        body: formData
                                    });
                                } else {
                                    await fetch(`${getCurrentUrl()}:5000/generate_spatial`, {
                                        method: 'POST',
                                        headers: {
                                            'Content-Type': 'application/json',
                                        },
                                        body: JSON.stringify({
                                            "distribution": spatialDistribution,
                                            "diagonalPercentage": diagonalPercentage / 100,
                                            "diagonalBuffer": diagonalBuffer,
                                            cardinality: numberNodes >  numberSpatialNodes ? numberNodes : numberSpatialNodes,
                                            maxX: maxX,
                                            maxY: maxY,
                                            "geometry": spatialGeometry,
                                            "srange": parcelSrange,
                                            "dither": parcelDither / 100,
                                            "probability": bitProbability / 100,
                                            "digits": bitDigits,
                                        }),
                                    })
                                }
                                await fetch(`${getCurrentUrl()}:5000/generate_combiner`, {
                                    method: 'POST',
                                    headers: {
                                        'Content-Type': 'application/json',
                                    },
                                    body: JSON.stringify({
                                        combiner: graphType,
                                        percentage: percentagePercentage / 100,
                                        numberSpatialNodes: numberSpatialNodes,
                                        mean: mean,
                                        std: std,
                                    }),
                                })
                                const collectGr = await fetch(`${getCurrentUrl()}:5000/collect_gr`, {
                                    method: 'POST',
                                    headers: {
                                        'Content-Type': 'application/json',
                                    },
                                    body: JSON.stringify({
                                        combiner: graphType,
                                        edgeLabels: edgeLabels.map(obj => {
                                            return {
                                                label: obj.label,
                                                percentage: obj.percentage / 100,
                                                color : obj.color
                                            }
                                        }),
                                    }),
                                })
                                const collectCO = await fetch(`${getCurrentUrl()}:5000/collect_co`, {
                                    method: 'POST',
                                    headers: {
                                        'Content-Type': 'application/json',
                                    },
                                    body: JSON.stringify({
                                        combiner: graphType,
                                    }),
                                })
                                debugger
                                props.setEdgeLabels(edgeLabels);
                                const socialFile = new File([await collectGr.blob()], "graph.gr");
                                const spatialFile = new File([await collectCO.blob()], "graph.co");
                                props.setCurrentCombiner(graphType);
                                props.handleUploadData(null, socialFile, spatialFile);
                                props.setIsLoading(false);
                                props.setUnsavedChanges(true);
                            }}>
                        Generate
                    </Button>

                </div>
                {props.openProperties &&
                    <div className="w-full border-t-2 mt-2 border-neutral-900 flex flex-col ">
                        {(props.jsonData.nodes.find(node => node.id === props.selectedObject?.id) ||
                                props.jsonData.links.find(link => {
                                    return (link?.source === (props.selectedObject?.source?.id ?? props.selectedObject?.source)) &&
                                        (link?.target === (props.selectedObject?.target?.id ?? props.selectedObject?.target))
                                }))
                            && <div>
                                <h2 className="text-xl dark:text-neutral-200  text-neutral-800 m-1 px-1  "> Properties
                                    : {props.selectedObject?.id ? 'Vertex ' + props.selectedObject?.id :
                                        ('Edge ' + (props.selectedObject?.source?.id ?? props.selectedObject?.source) + ' - ' +
                                            (props.selectedObject?.target?.id ?? props.selectedObject?.target))} </h2>
                                <hr className="h-px my-1 border-neutral-800 border-1 w-full flex-shrink-0"/>
                                <div className="my-4 mx-2 my-2 flex flex-col  mb-14">
                                    {props.selectedObject?.id &&
                                        <FormControl sx={{mb: 1}} fullWidth size="small">
                                            <InputLabel id="node-is-spatial-label">Vertex Type</InputLabel>
                                            <Select
                                                disabled={!props.editMode}
                                                labelId="demo-simple-select-label"
                                                id="demo-simple-select"
                                                value={currentNodeIsSpatial}
                                                label="Node Type"
                                                onChange={e => handleTypeChange(e.target.value)}
                                            >
                                                <MenuItem value={false}>Non-Spatial</MenuItem>
                                                <MenuItem value={true}>Spatial</MenuItem>
                                            </Select>
                                        </FormControl>
                                    }
                                    <TextField id="outlined-basic" label="Custom label" variant="outlined" size="small"
                                               disabled={!props.editMode}
                                               value={currentLabel} onChange={e => setCurrentLabel(e.target.value)}/>
                                    {currentNodeIsSpatial &&
                                        <div className="my-2">
                                            <TextField id="outlined-basic" sx={{my: 1}} label="X" variant="outlined"
                                                       size="small"
                                                       disabled={!props.editMode}
                                                       value={currentX} onChange={e => setCurrentX(e.target.value)}/>
                                            <TextField id="outlined-basic" sx={{my: 1}} label="Y" variant="outlined"
                                                       size="small"
                                                       disabled={!props.editMode}
                                                       value={currentY} onChange={e => setCurrentY(e.target.value)}/>
                                        </div>

                                    }
                                    {currentNodeIsSpatial && currentWidth &&
                                        <Stack direction="row" spacing={2}>
                                            <TextField id="outlined-basic" sx={{my: 1}} label="Width" variant="outlined"
                                                       size="small"
                                                       disabled={!props.editMode}
                                                       value={currentWidth}
                                                       onChange={e => setCurrentWidth(e.target.value)}/>
                                            <TextField id="outlined-basic" sx={{my: 1}} label="Height"
                                                       variant="outlined"
                                                       size="small"
                                                       disabled={!props.editMode}
                                                       value={currentHeight}
                                                       onChange={e => setCurrentHeight(e.target.value)}/>
                                        </Stack>
                                    }
                                    {
                                        props.selectedObject?.target &&
                                        <FormControl sx={{my: 2}} fullWidth size="small">
                                            <InputLabel id="edge-is-direct-label">Edge Type</InputLabel>
                                            <Select
                                                disabled={!props.editMode}
                                                labelId="edge-is-direct-label"
                                                id="demo-simple-select"
                                                value={currentEdgeIsDirected}
                                                label="Edge Type"
                                                onChange={e => setCurrentEdgeIsDirected(e.target.value)}
                                            >
                                                <MenuItem value={false}>Undirected</MenuItem>
                                                <MenuItem value={true}>Directed</MenuItem>
                                            </Select>
                                        </FormControl>
                                    }
                                    {props.selectedObject?.id && (typeof props.selectedObject?.fixedX === 'number') && props.selectedTab !=='combined'
                                        &&
                                        <svg id="propertiesSvg"
                                             className="w-full h-32 bg-neutral-200 rounded-lg my-2">
                                            <g id="propertiesZoomer">
                                                <g id="propertiesForceNodes"/>
                                                <g id="propertiesLabels"></g>
                                                <g id="propertiesStaticNodes"/>
                                            </g>
                                        </svg>
                                    }
                                    <Stack spacing={2} divider={<Divider orientation="vertical" flexItem/>}
                                           direction="row"
                                           justifyContent="center"
                                           className="flex-shrink-0"
                                           sx={{mt: 2}}>
                                        <Button component="label" variant="outlined"
                                                size="small"
                                                color="primary" startIcon={<SaveAsSharp/>}
                                                disabled={!props.editMode}
                                                onClick={changeObjectData}>
                                            Save
                                        </Button>
                                        <Button component="label" variant="outlined" color="secondary"
                                                size="small"
                                                disabled={!props.editMode} startIcon={<Delete/>}
                                                onClick={deleteSelectedObject}>
                                            Delete
                                        </Button>
                                        <Button component="label" variant="outlined"
                                                size="small"
                                                color="warning" startIcon={<CloseIcon/>}
                                                disabled={!props.editMode}
                                                onClick={cancelChanges}>
                                            Cancel
                                        </Button>
                                    </Stack>
                                </div>
                            </div>}
                    </div>

                }
            </div>

        </ThemeProvider>
    )
});

export default SideBar;